import axios from 'utils/axios';
import { createSlice } from '@reduxjs/toolkit';
import { getFilterDate } from 'utils/formatDateRange';

const initialState = {
  fetchUserList: {
    isLoading: false,
    error: false,
    data: [],
    count: 0,
  },
  fetchListDetail: {
    isLoading: false,
    error: false,
    data: null,
    users: [],
    count: 0,
  },
  fetchListById: {
    isLoading: false,
    error: false,
    data: null,
  },
  createUserList: {
    isLoading: false,
    error: false,
    success: false,
  },
  updateUserList: {
    isLoading: false,
    error: false,
    success: false,
  },
  deleteUser: {
    isLoading: false,
    error: false,
    success: false,
  },
  updateUser: {
    isLoading: false,
    error: false,
    success: false,
  },
  syncList: {
    isLoading: false,
    error: false,
    success: false,
  },
  createMailProviderList: {
    isLoading: false,
    error: false,
    success: false,
  },
};

const slice = createSlice({
  name: 'userList',
  initialState,
  reducers: {
    fetchUserListStart(state) {
      state.fetchUserList.isLoading = true;
      state.fetchUserList.error = false;
    },
    fetchUserListSuccess(state, action) {
      state.fetchUserList.isLoading = false;
      state.fetchUserList.data = action.payload.userLists;
      state.fetchUserList.count = action.payload.count;
    },
    fetchUserListError(state, action) {
      state.fetchUserList.isLoading = false;
      state.fetchUserList.error = action.payload;
    },
    fetchListByIdStart(state) {
      state.fetchListById.isLoading = true;
      state.fetchListById.error = false;
      state.fetchListById.data = null;
    },
    fetchListByIdSuccess(state, action) {
      state.fetchListById.isLoading = false;
      state.fetchListById.data = action.payload;
    },
    fetchListByIdError(state, action) {
      state.fetchListById.isLoading = false;
      state.fetchListById.error = action.payload;
    },
    fetchListDetailStart(state) {
      state.fetchListDetail.isLoading = true;
      state.fetchListDetail.error = false;
    },

    fetchListDetailSuccess(state, action) {
      state.fetchListDetail.isLoading = false;
      state.fetchListDetail.data = action.payload.mnList;
      state.fetchListDetail.users = action.payload.users;
      state.fetchListDetail.count = action.payload.count;
    },

    fetchListDetailError(state, action) {
      state.fetchListDetail.isLoading = false;
      state.fetchListDetail.error = action.payload;
    },
    createUserListStart(state) {
      state.createUserList.isLoading = true;
      state.createUserList.error = false;
      state.createUserList.success = false;
    },
    createUserListSuccess(state) {
      state.createUserList.isLoading = false;
      state.createUserList.success = true;
    },
    createUserListError(state, action) {
      state.createUserList.isLoading = false;
      state.createUserList.error = action.payload;
    },
    updateUserListStart(state) {
      state.updateUserList.isLoading = true;
      state.updateUserList.error = false;
      state.updateUserList.success = false;
    },
    updateUserListSuccess(state) {
      state.updateUserList.isLoading = false;
      state.updateUserList.success = true;
    },
    updateUserListError(state, action) {
      state.updateUserList.isLoading = false;
      state.updateUserList.error = action.payload;
      state.updateUserList.success = false;
    },
    deleteUserStart(state) {
      state.deleteUser.isLoading = true;
      state.deleteUser.error = false;
      state.deleteUser.success = false;
    },
    deleteUserSuccess(state, action) {
      state.deleteUser.isLoading = false;
      state.deleteUser.success = true;
    },
    deleteUserError(state, action) {
      state.deleteUser.isLoading = false;
      state.deleteUser.error = action.payload;
      state.deleteUser.success = false;
    },
    updateUserStart(state) {
      state.updateUser.isLoading = true;
      state.updateUser.error = false;
      state.updateUser.success = false;
    },
    updateUserSuccess(state) {
      state.updateUser.isLoading = false;
      state.updateUser.success = true;
    },
    updateUserError(state, action) {
      state.updateUser.isLoading = false;
      state.updateUser.error = action.payload;
      state.updateUser.success = false;
    },
    syncListStart(state) {
      state.syncList.isLoading = true;
      state.syncList.error = false;
      state.syncList.success = false;
    },
    syncListSuccess(state) {
      state.syncList.isLoading = false;
      state.syncList.success = true;
    },
    syncListError(state, action) {
      state.syncList.isLoading = false;
      state.syncList.error = action.payload;
      state.syncList.success = false;
    },
    createMailProviderListStart(state) {
      state.createMailProviderList.isLoading = true;
      state.createMailProviderList.error = false;
      state.createMailProviderList.success = false;
    },
    createMailProviderListSuccess(state) {
      state.createMailProviderList.isLoading = false;
      state.createMailProviderList.success = true;
    },
    createMailProviderListError(state, action) {
      state.createMailProviderList.isLoading = false;
      state.createMailProviderList.error = action.payload;
      state.createMailProviderList.success = false;
    },
  },
});

export default slice.reducer;

export const {
  fetchUserListStart,
  fetchUserListSuccess,
  fetchUserListError,
  fetchListByIdStart,
  fetchListByIdSuccess,
  fetchListByIdError,
  fetchListDetailStart,
  fetchListDetailSuccess,
  fetchListDetailError,
  createUserListStart,
  createUserListSuccess,
  createUserListError,
  updateUserListStart,
  updateUserListSuccess,
  updateUserListError,
  deleteUserError,
  deleteUserSuccess,
  deleteUserStart,
  updateUserStart,
  updateUserSuccess,
  updateUserError,
  syncListError,
  syncListSuccess,
  syncListStart,
  createMailProviderListStart,
  createMailProviderListSuccess,
  createMailProviderListError,
} = slice.actions;

export function getMNUserLists(appId, page, pageSize, search = []) {
  const updatedSearch = search.map((filter) => {
    if (filter.column === 'creationDate' || filter.column === 'lastSignIn') {
      const { start, end } = getFilterDate(parseInt(filter.additionalValue, 10), filter.value);

      if (filter.operator === '=' || filter.operator === '!=') {
        const newOperator = filter.operator === '=' ? 'between' : 'notBetween';
        return {
          ...filter,
          operator: newOperator,
          value: [start, end],
        };
      }

      return {
        ...filter,
        value: start,
      };
    }

    return { ...filter };
  });
  return async (dispatch) => {
    const searchQuery = updatedSearch.length > 0 ? `&search=${JSON.stringify(updatedSearch)}` : '';
    dispatch(fetchUserListStart());
    try {
      const response = await axios.get(
        `/monetization/${appId}/user-lists?order=creationDate${searchQuery}&ordering=desc&page=${page}&limit=${pageSize}`,
      );
      dispatch(fetchUserListSuccess(response));
    } catch (error) {
      dispatch(fetchUserListError(error.response?.data?.message || error.message));
    }
  };
}

export function getMNUserListById(appId, listId) {
  return async (dispatch) => {
    dispatch(fetchListByIdStart());
    try {
      const response = await axios.get(`/monetization/${appId}/user-lists/${listId}`);
      dispatch(fetchListByIdSuccess(response));
    } catch (error) {
      dispatch(fetchListByIdError(error.response?.data?.message || error.message));
    }
  };
}

export function createMNUserList(appId, data) {
  return async (dispatch) => {
    dispatch(createUserListStart());

    try {
      const response = await axios.post(`/monetization/${appId}/user-lists`, data);
      dispatch(createUserListSuccess(response));
    } catch (error) {
      dispatch(createUserListError(error.response?.data?.message || error.message));
    }
  };
}

export function editMNUserList(appId, listId, data) {
  return async (dispatch) => {
    dispatch(updateUserListStart());

    try {
      const response = await axios.put(`/monetization/${appId}/user-lists/${listId}`, data);
      dispatch(updateUserListSuccess(response));
    } catch (error) {
      dispatch(updateUserListError(error.response?.data?.message || error.message));
    }
  };
}

export function getMNUserListDetails(appId, listId, page, pageSize, search = []) {
  const updatedSearch = search.map((filter) => {
    if (filter.column === 'creationDate' || filter.column === 'lastSignIn') {
      const { start, end } = getFilterDate(parseInt(filter.additionalValue, 10), filter.value);

      if (filter.operator === '=' || filter.operator === '!=') {
        const newOperator = filter.operator === '=' ? 'between' : 'notBetween';
        return {
          ...filter,
          operator: newOperator,
          value: [start, end],
        };
      }

      return {
        ...filter,
        value: start,
      };
    }

    return { ...filter };
  });

  return async (dispatch) => {
    const searchQuery = updatedSearch.length > 0 ? `&search=${JSON.stringify(updatedSearch)}` : '';
    dispatch(fetchListDetailStart());
    try {
      const response = await axios.get(
        `/monetization/${appId}/user-lists/${listId}/details?order=creationDate${searchQuery}&ordering=desc&page=${page}&limit=${pageSize}`,
      );

      dispatch(fetchListDetailSuccess(response));
    } catch (error) {
      dispatch(fetchListDetailError(error.response?.data?.message || error.message));
    }
  };
}

export function updateMNUserList(appId, listId, data) {
  return async (dispatch) => {
    dispatch(createUserListStart());

    try {
      const response = await axios.put(`/monetization/${appId}/user-lists/${listId}`, data);
      dispatch(createUserListSuccess(response));
    } catch (error) {
      dispatch(createUserListError(error.response?.data?.message || error.message));
    }
  };
}

export function deleteMNUserList(appId, listId) {
  return async (dispatch) => {
    dispatch(createUserListStart());

    try {
      const response = await axios.delete(`/monetization/${appId}/user-lists/${listId}`);
      dispatch(createUserListSuccess(response));
    } catch (error) {
      dispatch(createUserListError(error.response?.data?.message || error.message));
    }
  };
}

export function deleteUserFromUserList(appId, listId, userId, type) {
  return async (dispatch) => {
    dispatch(deleteUserStart());

    try {
      const response = await axios.delete(`/monetization/${appId}/user-lists/${listId}/users/${userId}`);
      dispatch(deleteUserSuccess(response));
    } catch (error) {
      dispatch(deleteUserError(error.response?.data?.message || error.message));
    }
  };
}

export function updateUserInList(appId, listId, id, data) {
  return async (dispatch) => {
    dispatch(updateUserStart());

    try {
      const response = await axios.put(`/monetization/${appId}/user-lists/${listId}/users/${id}`, data);
      dispatch(updateUserSuccess(response));
      return response;
    } catch (error) {
      dispatch(updateUserError(error.response?.data?.message || error.message));
      throw error;
    }
  };
}

export function syncUserList(appId, listId, page, pageSize) {
  return async (dispatch) => {
    dispatch(syncListStart());
    try {
      const response = await axios.post(`/monetization/${appId}/user-lists/${listId}/sync`);
      dispatch(syncListSuccess());

      dispatch(getMNUserListDetails(appId, listId, page, pageSize));
      return response;
    } catch (error) {
      dispatch(syncListError(error.response?.data?.message || error.message));
      throw error;
    }
  };
}

export function createMailProviderList(appId, listId) {
  return async (dispatch) => {
    dispatch(createMailProviderListStart());
    try {
      const response = await axios.post(`/monetization/${appId}/user-lists/${listId}/create-provider-list`);
      dispatch(createMailProviderListSuccess());
      return response;
    } catch (error) {
      dispatch(createMailProviderListError(error.response?.data?.message || error.message));
      throw error;
    }
  };
}
